/**
 * 예산과목 연도별 이력
 */
import {
  isBdApiEnabled,
  POC_BUCKET,
  pocParams,
  upsertPocDoc,
  enqueuePersist,
} from 'services/pocRowPersist';

export const HISTORY_STORAGE_PREFIX = 'budgetSubjectHistory';

export function createSubjectUid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `SU-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * 목·세목 표기 정규화: `101-01` → `10101` (목 3자리+세목 2자리, 하이픈 제거)
 * 이미 붙어 있거나 다른 형식이면 그대로
 */
export function normalizeMokSemokCode(code) {
  const s = String(code ?? '').trim();
  if (/^\d{3}-\d{2}$/.test(s)) return s.replace('-', '');
  return s;
}

/** 상위 체인 (루트→현재) */
function buildAncestorChain(row, allRows = []) {
  const bt = row?.budgetType || '';
  const byKey = new Map(
    (allRows || []).map((r) => [`${r.budgetType || ''}::${r.subjectCode}`, r])
  );
  const chain = [];
  let cur = row;
  let guard = 0;
  while (cur && guard++ < 24) {
    chain.unshift(cur);
    if (!cur.parentCode) break;
    cur = byKey.get(`${bt}::${cur.parentCode}`) || null;
  }
  return chain;
}

/**
 * 경로코드
 * - 관·항·세항은 자기 코드만 (상위 생략: 항 `710`, 세항 `721`)
 * - 목·세목은 세항부터: `721-101`, `721-10101`
 * - 세목 코드가 목 코드를 포함하면 경로에서 목 생략
 */
export function buildPathCode(row, allRows = []) {
  if (!row?.subjectCode) return '';
  const chain = buildAncestorChain(row, allRows);
  if (!chain.length) return normalizeMokSemokCode(row.subjectCode);

  // 세항 있으면 세항부터, 없으면 항부터, 둘 다 없으면 관(루트)부터
  const sehangIdx = chain.findIndex((r) => r.levelName === '세항');
  const hangIdx = chain.findIndex((r) => r.levelName === '항');
  let from;
  if (sehangIdx >= 0) from = chain.slice(sehangIdx);
  else if (hangIdx >= 0) from = chain.slice(hangIdx);
  else from = chain;

  const codes = [];
  for (let i = 0; i < from.length; i += 1) {
    const r = from[i];
    const code = normalizeMokSemokCode(r.subjectCode);
    if (r.levelName === '목') {
      const next = from[i + 1];
      if (next?.levelName === '세목') {
        const nextCode = normalizeMokSemokCode(next.subjectCode);
        if (nextCode.startsWith(code)) continue;
      }
    }
    codes.push(code);
  }
  return codes.join('-');
}

/**
 * 편성·조회용 결합 표시코드
 * - 세항+세목: `721-10101` (동일 10101이라도 세항으로 구분)
 * - 세항+목(수입 등 세목 없음): `721-101` 또는 `611-61107`
 * - 세항만: `721`
 */
export function buildSubjectDispCode(row, allRows = []) {
  if (!row) return '';
  const chain = buildAncestorChain(row, allRows);
  const sehang = chain.find((r) => r.levelName === '세항');
  const mok = chain.find((r) => r.levelName === '목');
  const semok = chain.find((r) => r.levelName === '세목');

  if (sehang && semok) {
    const semokCode = normalizeMokSemokCode(semok.subjectCode);
    const mokCode = mok ? normalizeMokSemokCode(mok.subjectCode) : '';
    const mokSemok =
      mokCode && !semokCode.startsWith(mokCode) ? `${mokCode}${semokCode}` : semokCode;
    return `${sehang.subjectCode}-${mokSemok}`;
  }
  if (sehang && mok) {
    return `${sehang.subjectCode}-${normalizeMokSemokCode(mok.subjectCode)}`;
  }
  if (sehang) return String(sehang.subjectCode);
  return normalizeMokSemokCode(row.subjectCode) || String(row.pathCode || '');
}

/**
 * 편성·조회용 결합 과목명
 * - 세항+세목: `제품제조비 - 보수`
 * - 세항+목만: `세항명 - 목명`
 * - 그 외: 현재 행 과목명
 */
export function buildSubjectDispName(row, allRows = []) {
  if (!row) return '';
  const chain = buildAncestorChain(row, allRows);
  const sehang = chain.find((r) => r.levelName === '세항');
  const mok = chain.find((r) => r.levelName === '목');
  const semok = chain.find((r) => r.levelName === '세목');

  if (sehang && semok) {
    const a = String(sehang.subjectName || sehang.subjectCode || '').trim();
    const b = String(semok.subjectName || semok.subjectCode || '').trim();
    if (a && b) return `${a} - ${b}`;
  }
  if (sehang && mok) {
    const a = String(sehang.subjectName || sehang.subjectCode || '').trim();
    const b = String(mok.subjectName || mok.subjectCode || '').trim();
    if (a && b) return `${a} - ${b}`;
  }
  return String(row.subjectName || row.name || '').trim();
}

/** 편성 후보: 최종레벨(finalYn) 또는 자식 없는 리프 */
export function filterSubjectLeaves(rows = []) {
  const list = rows || [];
  const childParents = new Set(
    list.filter((r) => r.mod !== 'D' && r.parentCode).map((r) => `${r.budgetType || ''}::${r.parentCode}`)
  );
  return list.filter((r) => {
    if (r.mod === 'D' || r.useYn === 'N') return false;
    if (r.finalYn === 'Y') return true;
    const key = `${r.budgetType || ''}::${r.subjectCode}`;
    return !childParents.has(key);
  });
}

/** 행에 subjectUid·pathCode 보강 */
export function ensureSubjectIdentity(row, allRows = []) {
  const subjectCode = normalizeMokSemokCode(row.subjectCode) || row.subjectCode;
  const parentCode = normalizeMokSemokCode(row.parentCode) || row.parentCode || '';
  const subjectUid = row.subjectUid || createSubjectUid();
  const normalized = { ...row, subjectCode, parentCode, subjectUid };
  const pathCode = buildPathCode(normalized, allRows);
  return { ...normalized, pathCode };
}

export function ensureSubjectsIdentity(rows = []) {
  const withUid = (rows || []).map((r) => {
    const subjectCode = normalizeMokSemokCode(r.subjectCode) || r.subjectCode;
    const parentCode = normalizeMokSemokCode(r.parentCode) || r.parentCode || '';
    return {
      ...r,
      subjectCode,
      parentCode,
      subjectUid: r.subjectUid || createSubjectUid(),
    };
  });
  // 경로코드는 항상 재계산 (세항 기준 축약·세목 표기 반영)
  return withUid.map((r) => ({
    ...r,
    pathCode: buildPathCode(r, withUid),
  }));
}

export function historyStorageKey(tenantCode, subjectUid) {
  return [HISTORY_STORAGE_PREFIX, tenantCode || '', subjectUid || ''].join('|');
}

export function loadSubjectHistory(tenantCode, subjectUid) {
  if (!subjectUid) return [];
  try {
    const raw = localStorage.getItem(historyStorageKey(tenantCode, subjectUid));
    if (!raw) return [];
    const data = JSON.parse(raw);
    const rows = Array.isArray(data?.rows) ? data.rows : [];
    return rows.sort((a, b) => Number(b.fiscalYear) - Number(a.fiscalYear));
  } catch {
    return [];
  }
}

export function saveSubjectHistory(tenantCode, subjectUid, rows) {
  if (!subjectUid) return;
  const key = historyStorageKey(tenantCode, subjectUid);
  const value = {
    tenantCode,
    subjectUid,
    rows: rows || [],
    savedAt: new Date().toISOString(),
  };
  localStorage.setItem(key, JSON.stringify(value));
  if (isBdApiEnabled()) {
    enqueuePersist(key, () =>
      upsertPocDoc({
        bucket: POC_BUCKET.SUBJECT_HIST,
        rowUid: subjectUid,
        payload: value,
        params: pocParams({ tenantCode }),
      })
    );
  }
}

/** 과목별 이력 키가 브라우저 할당량을 넘기지 않도록 일괄 삭제 */
export function purgeBudgetSubjectHistory() {
  if (typeof localStorage === 'undefined') return;
  const prefix = `${HISTORY_STORAGE_PREFIX}|`;
  const keys = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const k = localStorage.key(i);
    if (k && k.startsWith(prefix)) keys.push(k);
  }
  keys.forEach((k) => localStorage.removeItem(k));
}

/**
 * 현재 연도 과목 스냅샷을 이력에 upsert
 * @returns {object[]} 갱신된 이력 목록
 */
export function upsertCurrentYearHistory(tenantCode, subjectRow, fiscalYear) {
  if (!subjectRow?.subjectUid) return [];
  const fy = String(fiscalYear || '');
  const prev = loadSubjectHistory(tenantCode, subjectRow.subjectUid);
  const snap = {
    fiscalYear: fy,
    budgetType: subjectRow.budgetType || '',
    levelName: subjectRow.levelName || '',
    subjectCode: subjectRow.subjectCode || '',
    subjectName: subjectRow.subjectName || '',
    subjectUid: subjectRow.subjectUid,
    pathCode: subjectRow.pathCode || '',
    useYn: subjectRow.useYn || 'Y',
    remark: subjectRow.remark || '',
  };
  const idx = prev.findIndex(
    (r) => String(r.fiscalYear) === fy && (r.budgetType || '') === (snap.budgetType || '')
  );
  let next;
  if (idx >= 0) {
    next = [...prev];
    next[idx] = { ...next[idx], ...snap };
  } else {
    next = [snap, ...prev];
  }
  next.sort((a, b) => Number(b.fiscalYear) - Number(a.fiscalYear));
  saveSubjectHistory(tenantCode, subjectRow.subjectUid, next);
  return next;
}

/**
 * PoC: 과거 2~3개 연도 샘플 이력이 없으면 현재 스냅샷 기반으로 생성
 * (코드만 살짝 다르게 보여 연도간 변경을 시연)
 */
export function ensureSampleHistory(tenantCode, subjectRow, currentFiscalYear, yearsBack = 3) {
  if (!subjectRow?.subjectUid) return [];
  let list = loadSubjectHistory(tenantCode, subjectRow.subjectUid);
  const fy = Number(currentFiscalYear);
  if (!Number.isFinite(fy)) return list;

  const upserted = upsertCurrentYearHistory(tenantCode, subjectRow, String(fy));
  list = upserted;

  for (let i = 1; i <= yearsBack; i += 1) {
    const y = String(fy - i);
    const exists = list.some(
      (r) => String(r.fiscalYear) === y && (r.budgetType || '') === (subjectRow.budgetType || '')
    );
    if (exists) continue;
    list.push({
      fiscalYear: y,
      budgetType: subjectRow.budgetType || '',
      levelName: subjectRow.levelName || '',
      subjectCode: subjectRow.subjectCode || '',
      subjectName: subjectRow.subjectName || '',
      subjectUid: subjectRow.subjectUid,
      pathCode: subjectRow.pathCode || '',
      useYn: 'Y',
      remark: `${y}년 확정 스냅샷(샘플)`,
    });
  }
  list.sort((a, b) => Number(b.fiscalYear) - Number(a.fiscalYear));
  saveSubjectHistory(tenantCode, subjectRow.subjectUid, list);
  return list;
}

/** 다건 과목의 현재연도 이력을 일괄 upsert */
export function upsertHistoryForSubjects(tenantCode, subjectRows, fiscalYear) {
  (subjectRows || []).forEach((r) => {
    if (r?.subjectUid && r?.subjectCode) {
      upsertCurrentYearHistory(tenantCode, r, fiscalYear);
    }
  });
}
